import {
  Alert,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import { Asset } from '../types'
import { NavLink } from 'react-router-dom'
import { humanizeTimestamp } from '../utils/date'
import { ArrowForward, Circle } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import AssetStatusIndicator from './AssetStatusIndicator'
import { useState } from 'react'

export interface AssetHistoryListProps {
  assetHistory: Record<string, Asset>
}

export default function AssetHistoryList({ assetHistory }: AssetHistoryListProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The local state.
   */
  const [_assetHistory, _setAssetHistory] = useState<Record<string, Asset>>({
    ...assetHistory
  })

  /**
   * Handle the after delete action.
   */
  const _handleAfterDelete = (deletedAsset: Asset) => {
    const newAssetHistory = { ..._assetHistory }

    Object.keys(_assetHistory).forEach((timestamp) => {
      if (assetHistory[timestamp].id === deletedAsset.id) {
        delete newAssetHistory[timestamp]
      }
    })

    _setAssetHistory(newAssetHistory)
  }

  return (
    <>
      {Object.keys(assetHistory).length === 0 ? (
        <Alert severity="info">{t('noAssetHistory')}</Alert>
      ) : (
        <List>
          {Object.keys(assetHistory)
            .reverse()
            .map((timestamp) => (
              <ListItem disablePadding disableGutters key={assetHistory[timestamp].id}>
                <ListItemButton component={NavLink} to={`/assets/${assetHistory[timestamp].id}`}>
                  <ListItemText
                    secondary={humanizeTimestamp(timestamp)}
                    primary={
                      <Stack direction="column" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <AssetStatusIndicator asset={assetHistory[timestamp]} />
                          <Typography sx={{ fontWeight: 500 }}>
                            {assetHistory[timestamp].name}
                          </Typography>

                          {assetHistory[timestamp]?.device?.powerSupply?.fuse && (
                            <>
                              <Circle sx={{ fontSize: '6px' }} />
                              <Typography sx={{ fontWeight: 500 }}>
                                {assetHistory[timestamp]?.device?.powerSupply?.fuse}A
                              </Typography>
                            </>
                          )}
                        </Stack>

                        <Stack direction="row" spacing={3}>
                          {assetHistory[timestamp]?.company?.name && (
                            <Chip
                              size="small"
                              label={assetHistory[timestamp]?.company?.name ?? '-'}
                            />
                          )}

                          {assetHistory[timestamp]?.project?.name && (
                            <Chip
                              size="small"
                              label={assetHistory[timestamp]?.project?.name ?? '-'}
                            />
                          )}
                        </Stack>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <ArrowForward />
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      )}
    </>
  )
}
